/**
 * Sentry Error Tracking Configuration
 *
 * Real-time error tracking and monitoring for production
 * Only enabled in production environments
 */

import * as Sentry from '@sentry/react';
import { isProduction, isDevelopment } from './logger';

/**
 * Initialize Sentry error tracking
 * Only runs in production environments
 */
export const initSentry = () => {
  // Only initialize in production
  if (!isProduction()) {
    if (isDevelopment()) {
      console.info('[Sentry] Disabled in development environment');
    }
    return;
  }

  const dsn = process.env.REACT_APP_SENTRY_DSN;

  if (!dsn) {
    console.error(
      '[Sentry] REACT_APP_SENTRY_DSN not found in environment variables. ' +
        'Add it to .env.production to enable error tracking.'
    );
    return;
  }

  try {
    Sentry.init({
      dsn,
      environment: 'production',

      // Performance Monitoring
      tracesSampleRate: 0.1, // Capture 10% of transactions for performance monitoring

      // Release tracking (optional - helps track which version has errors)
      release: process.env.REACT_APP_VERSION || 'unknown',

      // Before sending events, you can modify them here
      beforeSend(event, hint) {
        // Filter out errors from browser extensions
        if (event.exception) {
          const error = hint.originalException;
          if (
            error &&
            error.message &&
            error.message.includes('chrome-extension://')
          ) {
            return null; // Don't send to Sentry
          }
        }
        return event;
      },

      // Ignore certain errors
      ignoreErrors: [
        // Browser extension errors
        'chrome-extension://',
        'moz-extension://',
        // Network errors (often not actionable)
        'Network request failed',
        'NetworkError',
        // ResizeObserver errors (common, usually harmless)
        'ResizeObserver loop limit exceeded',
        // Script loading errors (often ad blockers)
        'Script error',
      ],
    });

    // console.info('[Sentry] Initialized successfully');
  } catch (error) {
    console.error('[Sentry] Failed to initialize:', error);
  }
};

/**
 * Manually capture an exception
 * @param {Error} error - The error to capture
 * @param {Object} context - Additional context
 */
export const captureException = (error, context = {}) => {
  if (!isProduction()) {
    console.error('[Sentry] Would capture exception:', error, context);
    return;
  }

  Sentry.captureException(error, {
    extra: context,
  });
};

/**
 * Manually capture a message
 * @param {string} message - The message to capture
 * @param {string} level - Severity level (info, warning, error)
 * @param {Object} context - Additional context
 */
export const captureMessage = (message, level = 'info', context = {}) => {
  if (!isProduction()) {
    console.log(`[Sentry] Would capture message (${level}):`, message, context);
    return;
  }

  Sentry.captureMessage(message, {
    level,
    extra: context,
  });
};

/**
 * Set user context for error tracking
 * @param {Object} user - User information
 */
export const setUser = (user) => {
  if (!isProduction()) {
    console.log('[Sentry] Would set user:', user);
    return;
  }

  Sentry.setUser(user);
};

/**
 * Add breadcrumb for debugging
 * @param {Object} breadcrumb - Breadcrumb data
 */
export const addBreadcrumb = (breadcrumb) => {
  if (!isProduction()) {
    console.log('[Sentry] Would add breadcrumb:', breadcrumb);
    return;
  }

  Sentry.addBreadcrumb(breadcrumb);
};

export default Sentry;
